<template>
    <loader :loading="loadingBanks || loadingCards || loadingPayment" class="w-100" transparent>
        <div v-if="details">
            <div>
                <div class="row mb-2">
                    <div class="col font-18 text-primary font-weight-bold">{{ $t('Make Payment') }}</div>
                </div>

                <div class="row row-cols-2 mb-2">
                    <div class="col text-left">{{ $t('Minimum Due') }}</div>
                    <div class="col text-right">
                        <fn-format-currency :value="displayedMinimumDue"></fn-format-currency>
                    </div>
                </div>

                <div class="row row-cols-2 mb-4">
                    <div class="col text-left">{{ $t('On or Before') }}</div>
                    <div class="col text-right">
                        <fn-format-date format="MM/DD/YYYY" :value="pending.pay_at" :ignore-time-zone="true"></fn-format-date>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col">
                        <label class="mb-0 d-flex justify-content-between align-items-center w-100" for="pay_amount">
                            <span>{{ $t('Payment Amount') }}</span>
                            <span v-if="isPayoff" class="badge badge-primary font-10 d-flex align-items-center">
                                <icon icon="lock" size="xs" class="mr-1"></icon>
                                {{ $t('Locked to Payoff') }}
                            </span>
                        </label>
                        <fn-type-money-input
                            id="pay_amount"
                            :format="'$0,0.00'"
                            v-model="payAmount"
                            :minLength="isPayoff ? payoffAmount : initialPayAmount"
                            :maxLength="payoffAmount <= 0 ? MAX_PAYABLE_AMOUNT : payoffAmount"
                            :disabled="isPayoff"
                            required
                        ></fn-type-money-input>
                    </div>
                </div>

                <div class="or-divider d-flex align-items-center my-3">
                    <div class="or-divider__line flex-grow-1"></div>
                    <span class="or-divider__label px-3">{{ $t('OR') }}</span>
                    <div class="or-divider__line flex-grow-1"></div>
                </div>

                <div class="row mb-3">
                    <div class="col">
                        <div
                            class="payoff-toggle d-flex align-items-center p-3 rounded"
                            :class="isPayoff ? 'payoff-toggle--active' : 'payoff-toggle--inactive'"
                            @click="isPayoff = !isPayoff"
                        >
                            <div class="payoff-toggle__icon mr-3" :class="isPayoff ? 'text-primary' : 'text-muted'">
                                <icon icon="check-circle" size="2x"></icon>
                            </div>
                            <div class="flex-grow-1">
                                <div class="font-weight-bold font-14" :class="isPayoff ? 'text-primary' : 'text-dark'">
                                    {{ $t('Pay Off My Lease') }}
                                </div>
                                <div class="mt-1 d-flex align-items-center flex-wrap">
                                    <span class="font-13 text-muted mr-1">{{ $t('Full balance') }}:</span>
                                    <span class="font-14 font-weight-bold" :class="isPayoff ? 'text-primary' : 'text-dark'">
                                        <fn-format-currency :value="payoffAmount"></fn-format-currency>
                                    </span>
                                </div>
                            </div>
                            <b-form-checkbox
                                v-model="isPayoff"
                                switch
                                size="lg"
                                class="mb-0 ml-3 flex-shrink-0"
                                :disabled="payoffAmount <= 0"
                                @click.native.stop
                            ></b-form-checkbox>
                        </div>
                    </div>
                </div>

                <div class="section-label d-flex align-items-center mb-3 mt-1">
                    <span class="section-label__text mr-3">{{ $t('Pay With') }}</span>
                    <div class="section-label__line flex-grow-1"></div>
                </div>

                <div class="row mb-2">
                        <div class="col" v-if="!loadingBanks && !loadingCards">
                            <payment-select
                                :cards="customerCards"
                                :banks="customerBanks"
                                :default="defaultPayment"
                                @payment-change="handlePaymentChange"
                                @add-card="handleAddCard"
                            ></payment-select>
                        </div>
                </div>

                <div class="row mb-2">
                    <div class="col">
                        <label class="mb-0" for="pay_date">{{ $t('Payment Date') }}</label>
                        <fn-type-date-input
                            id="pay_date"
                            :data="dateInput"
                            v-model="payDate"
                        ></fn-type-date-input>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col">
                        <div class="btn btn-block btn-lg px-5 d-flex justify-content-between align-items-center"
                            :class="isDisabled ? 'disabled btn-outline-dark' : isPayoff ? 'btn-primary' : 'btn-outline-dark'"
                            @click="handlePayment">
                            <span class="font-14">{{ isPayoff ? $t('Pay Off Lease') : $t('Make Payment') }}</span>
                            <icon class="ml-auto" :icon="isPayoff ? 'check-circle' : 'long-arrow-right'" size="xl"></icon>
                        </div>
                    </div>
                </div>

                <div v-if="isSelectedPaymentExpired" class="row mb-3">
                    <div class="col">
                        <div class="d-flex align-items-center p-3 rounded border border-warning" style="background: #fffbeb;">
                            <icon icon="exclamation-triangle" class="text-warning mr-2 flex-shrink-0"></icon>
                            <span class="font-13 text-dark flex-grow-1">{{ $t('Your selected card is expired.') }}</span>
                            <button
                                class="btn btn-sm btn-outline-primary d-flex align-items-center ml-2 flex-shrink-0"
                                @click="handleAddCard"
                            >
                                <icon icon="plus-circle" class="mr-1"></icon>
                                {{ $t('Add New Card') }}
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </loader>
</template>

<script>
import {FnTypeMoneyInput, FnTypeDateInput} from "@fndry-vue/fn-types";
import withContext from "@fndry-vue/fn-context/mixins/withContext";
import {withScreen} from "@fndry-vue/fn-screen";
import customerDetails from "../../dashboard/mixins/customerDetails";
import moment from "moment";
import {DASHBOARD, MAX_PAYABLE_AMOUNT} from "../../dashboard";
import PaymentSelect from "../../dashboard/components/PaymentSelect";
import { isCardExpired } from "../../dashboard/utils/cardExpiry";
// import AddNewCardPanel from "../../profile/panels/AddNewCardPanel";

export default {
    name: "PayEarlyScreen",
    props: {
        details: Object,
        lease: Object,
        pending: Object,
        backRouteName: {
            type: String,
            default : DASHBOARD
        }
    },
    mixins: [
        withContext,
        withScreen,
        customerDetails,
    ],
    components: {
        FnTypeMoneyInput,
        FnTypeDateInput,
        PaymentSelect
    },
    screen() {
        return {
            class: 'details-screen',
            data: {
                withButton: false,
                noToggle: true,
                withBack: true,
                backRouteName: this.backRouteName,
                details : this.details,
            }
        }
    },
    context(){
        return false;
    },
    data() {
        return {
            payAmount: 0,
            payDate: null,
            selectedPayment_: null,
            loadingPayment: false,
            displayedMinDue: 0,
            isPayoff: false,
        }
    },
    watch: {
        isPayoff(val) {
            if (val) {
                this.payAmount = this.payoffAmount;
            } else {
                this.payAmount = this.initialPayAmount;
            }
        }
    },
    created() {
        if(this.pending === undefined || this.lease === undefined) {
            this.$router.push({
                name: this.backRouteName
            })
        }
    },
    mounted() {
        this.payAmount = this.initialPayAmount;
        this.displayedMinDue = this.payAmount;
    },
    computed: {
        displayedMinimumDue() {
            return this.parseNumber(this.displayedMinDue);
        },
        minimumDue() {
            return this.parseNumber(this.pending.minDue);
        },
        initialPayAmount() {
            return this.parseNumber(this.pending?.amount? this.pending?.amount : this.pending?.amount_ach);
        },
        payoffAmount() {
            return this.parseNumber(this.lease?.extra?.balance);
        },
        dateInput() {
            return {
                required: true,
                rules: 'required',
                datetime: {
                    minDate: moment().toDate(),
                    maxDate: this.pending.pay_at ? moment(this.pending.pay_at).toDate() : null,
                }
            }
        },
        isSelectedPaymentExpired() {
            const card = this.selectedPayment;
            if (!card || card.payment_type !== 'card') return false;
            return isCardExpired(card);
        },
        isDisabled() {
            return this.payAmount === null || this.payDate === null || this.selectedPayment === null || this.selectedPayment?.id === null || this.isSelectedPaymentExpired;
        },
        selectedPayment: {
            get() {
                if(this.selectedPayment_) {
                    return this.selectedPayment_
                } else {
                    return this.defaultPayment;
                }
            },
            set(value) {
                this.selectedPayment_ = value;
            }
        }
    },
    methods: {
        parseNumber(value) {
            const parsedValue = parseFloat(value);
            return isNaN(parsedValue) ? 0 : parsedValue;
        },
        handlePaymentChange(payment) {
            this.selectedPayment = payment;
        },
        isPayoffAmount(amount) {
            return this.parseNumber(amount) >= this.parseNumber(this.lease.extra.balance);
        },
        handlePayment() {

            if(this.isDisabled || this.loadingPayment) {
                return;
            }

            this.loadingPayment = true;

            if(this.payAmount >= this.minimumDue && this.payAmount <= this.payoffAmount && this.payAmount <= MAX_PAYABLE_AMOUNT) {
                let text = this.isPayoff
                    ? this.$t("lease.pay_early.payoff_confirm", {amount: this.payAmount, date: moment(this.payDate).format('MM/DD/YYYY')})
                    : this.$t("lease.pay_early.confirm", {amount: this.payAmount, date: moment(this.payDate).format('MM/DD/YYYY')});
                let description = this.isPayoff
                    ? this.$t("lease.pay_early.payoff_description")
                    : this.$t("lease.pay_early.description");
                this.$fnModals.confirm(text + " " + description)
                    .then(value => {
                        if(value) {
                            this.$fnApi.call('/api/v1/mobile/payments', 'POST', {
                                amount : this.payAmount,
                                method_id : this.selectedPayment.id,
                                method_type : this.selectedPayment.payment_type,
                                date: this.payDate,
                                lease: this.lease.lease_uuid,
                                isPayoffAmount: this.isPayoffAmount(this.payAmount),
                            }).then(response => {
                                if(response.status) {
                                    if(response?.data?.['scheduled']) {
                                        this.$fnNotify({
                                            message: this.$t("Your payment has been scheduled"),
                                            type: "success"
                                        });
                                    }
                                    else {
                                        this.$fnNotify({
                                            message: this.$t("Your payment has been successfully processed, please allow up to 3 business days for the payment to post to your account."),
                                            type: "success"
                                        });
                                    }
                                    this.loadingPayment = false;
                                    this.$router.push({
                                        name: this.backRouteName
                                    })
                                } else {
                                    this.$fnNotify({
                                        message: this.$t("An unknown error occurred, please try again later!"),
                                        type: "error"
                                    });
                                    this.loadingPayment = false;
                                }
                            })
                        } else {
                            this.loadingPayment = false;
                        }
                    })
            } else {
                this.$fnNotify({
                    message: this.$t("lease.pay_early.warning", {'minimumDue': this.minimumDue, 'maxAmount': this.payoffAmount > 0 ? this.payoffAmount : MAX_PAYABLE_AMOUNT}),
                    type: "error"
                });
                this.loadingPayment = false;
            }
        },
        handleAddCard() {
            this.$fnPanels.open({
                component: () => import("../../profile/panels/AddNewCardPanel"),
                props: {
                    ssn: this.lease.social_security_number
                },
                on: {
                    success: () => {
                        this.selectedPayment = null;
                        this.getCustomerCards()
                    }
                }
            })
        }
    }
}
</script>

<style scoped>
.or-divider__line {
    height: 1px;
    background-color: #dee2e6;
}
.or-divider__label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #adb5bd;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
}
.section-label__text {
    font-size: 0.7rem;
    font-weight: 700;
    color: #adb5bd;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
}
.section-label__line {
    height: 1px;
    background-color: #dee2e6;
}
.payoff-toggle__icon {
    transition: color 0.2s ease;
    line-height: 1;
}
.payoff-toggle {
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    border: 2px solid;
    user-select: none;
}
.payoff-toggle--active {
    border-color: var(--primary, #007bff);
    background-color: rgba(0, 123, 255, 0.05);
}
.payoff-toggle--inactive {
    border-color: #dee2e6;
    background-color: #f8f9fa;
}
.payoff-toggle--active:hover {
    background-color: rgba(0, 123, 255, 0.08);
}
.payoff-toggle--inactive:hover {
    background-color: #f1f3f5;
    border-color: #adb5bd;
}
</style>
